import React, { useContext, useEffect } from "react";
import {
    AutocompleteDropdown,
    ClearFiltersButton,
    ContentSection,
    DateRangePicker,
    DetailsRowProps,
    DropdownMultiselect,
    ensure,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    MultiselectSearchDropdown,
    RenderFunction,
    SearchBox,
    Stack,
    Theme
} from "sprint-common-web";

import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ordersRoutes } from "../../OrdersRoutes";
import { OrderRequestListingContext } from "./context/OrderRequestListingContext";
import { createOrderRequestListingButtons } from "./createOrderRequestListingButtons";
import { createOrderRequestListingColumns } from "./createOrderRequestListingColumns";
import { useOrderRequestListingFilters } from "./hooks/useOrderRequestListingFilters";
import { OrderRequestListingItem } from "./OrderRequestListingItem";

export const OrderRequestListing: React.VFC = () => {
    const {
        filters,
        sourceOptions,
        airlineOptions,
        systemOptions,
        progressOptions,
        assetsAssociationStatesOptions,
        filterDistributorOptions,
        setSort,
        setPage,
        setPageSize,
        setStatuses,
        setSources,
        clearFilters,
        setContent,
        setFilename,
        setDistributor,
        setAirlines,
        setSystems,
        setPONumber,
        setProgresses,
        setDueDateStart,
        setDueDateEnd,
        setAssetsAssociationStates,
        statusOptions
    } = useOrderRequestListingFilters();

    const { items, totalItems, isLoading, reloadData, setSelectedItems, selectedItems } = useContext(
        OrderRequestListingContext
    );

    useEffect(() => {
        reloadData(filters);
    }, [filters]);

    const columns = createOrderRequestListingColumns();
    const buttons = createOrderRequestListingButtons({ filters, setPageSize });

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ordersRoutes} />
            </PageHeader>
            <ContentSection header="Requests" buttons={buttons}>
                <Grid>
                    <GridRow>
                        <Stack horizontalAlign="end">
                            <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                        </Stack>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchBox
                                testId="orders-requests-filters-poNumber"
                                placeholder="PO Number"
                                onChange={setPONumber}
                                value={filters.pONumber}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                testId="order-requests-filters-content"
                                placeholder="Content"
                                onChange={setContent}
                                value={filters.content}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                testId="order-requests-filters-filename"
                                placeholder="Filename"
                                onChange={setFilename}
                                value={filters.filename}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <AutocompleteDropdown
                                testId="order-requests-filters-distributor"
                                placeholder="Distributor"
                                selectedKey={filters.distributorId}
                                selectedText={filters.distributorName}
                                onChange={setDistributor}
                                onSearch={filterDistributorOptions}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                testId="order-requests-filters-status"
                                placeholder="Status"
                                selectedKeys={filters.statuses}
                                onChange={setStatuses}
                                options={statusOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                testId="order-requests-filters-source"
                                placeholder="Source"
                                selectedKeys={filters.sources}
                                onChange={setSources}
                                options={sourceOptions}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                testId="orders-filters-progress"
                                placeholder="Progress"
                                selectedKeys={filters.progresses}
                                onChange={setProgresses}
                                options={progressOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                options={airlineOptions}
                                placeholder="Airlines"
                                onChange={setAirlines}
                                selectedKeys={filters.airlineIds}
                                disabled={!airlineOptions.length}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                options={systemOptions}
                                placeholder="Systems"
                                onChange={setSystems}
                                selectedKeys={filters.systemIds}
                                disabled={!filters.airlineIds?.length}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                options={assetsAssociationStatesOptions}
                                placeholder="Assets association state"
                                selectedKeys={filters.assetsAssociationStates}
                                onChange={setAssetsAssociationStates}
                                testId="order-requests-filters-assets-association-state"
                            />
                        </GridColumn>

                        <GridColumn sm={4}>
                            <DateRangePicker
                                onDateStartSelect={setDueDateStart}
                                onDateEndSelect={setDueDateEnd}
                                startValue={filters.dueDateStart ? new Date(filters.dueDateStart) : undefined}
                                endValue={filters.dueDateEnd ? new Date(filters.dueDateEnd) : undefined}
                                fromPlaceholder="Due date from"
                                toPlaceholder="Due date to"
                                testId="order-requests-filters-date-range"
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                selectedItems={selectedItems}
                                items={items}
                                isLoading={isLoading}
                                pagination={{
                                    page: filters.page,
                                    pageSize: filters.pageSize,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                onRenderRow={renderRow}
                                onSelectionChange={setSelectedItems}
                                layoutMode="fixedColumns"
                                isStickyModeEnabled={true}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const renderRow = (rowProps?: DetailsRowProps, defaultRender?: RenderFunction<DetailsRowProps>) => {
    ensure(rowProps);
    ensure(defaultRender);

    const item = rowProps.item as OrderRequestListingItem;
    const testProps = {
        "data-testid": "table-row",
        "data-testkey": rowProps.item.id
    };

    if (item.isModified) {
        return renderCustomRow(rowProps, defaultRender, RowType.Modified);
    }

    if (item.isPinned) {
        return renderCustomRow(rowProps, defaultRender, RowType.Pinned);
    }

    return defaultRender({ ...testProps, ...rowProps }, defaultRender);
};

enum RowType {
    Modified = 1,
    Pinned = 3
}

const colorsByRowType = {
    [RowType.Modified]: Theme.palette.blue,
    [RowType.Pinned]: Theme.palette.green
};

const renderCustomRow = (
    rowProps: DetailsRowProps,
    defaultRender: RenderFunction<DetailsRowProps>,
    type: RowType
): JSX.Element | null => {
    return defaultRender({
        ...rowProps,
        styles: {
            root: {
                userSelect: "auto",
                borderLeft: `4px solid ${colorsByRowType[type]}`
            }
        }
    });
};
